import { gzip, ungzip } from 'pako';
import {
  base64ToBytes,
  bytesToBase64,
  crockfordBase32Decode,
  crockfordBase32Encode,
  uint32ToLittleEndianBytes,
} from '@trimble/encoding-utils';

const CSIB_URN_PREFIX = 'urn:trimble:def:crs:ID::';
const CSIB_MAGIC = 'TNL CSIB';
const LENGTH_PREFIX_BYTES = 4;

/**
 * Gzip is not byte-stable across encoders, and the backend hard-checks this
 * exact WGS84 base32, so WGS84 is recognized and substituted, never recompressed.
 */
const WGS84_CSIB_BASE64 =
  'VE5MIENTSUIAAAAAAAAmQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAABXR1MgMTk4NAAAV29ybGQgR2VvZGV0aWMgU3lzdGVtIDE5ODQAAAAAAECmVFhB0ZccFMQ/WEEFAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPA/AAAAAAAA8D8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABgtRFT7Ifm/GC1EVPshCUAYLURU+yH5PxgtRFT7IQlAAQEBAQEBAwAAAAAAAAAAVwBHAFMAIAAxADkAOAA0AAAAVwBvAHIAbABkACAARwBlAG8AZABlAHQAaQBjACAAUwB5AHMAdABlAG0AIAAxADkAOAA0AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

const WGS84_BASE32 =
  '8W0G000ZHC400000000002GBY7SN2W0EYST640036M3GH38B60173E3VP21A39C51689AQWMJEHE17MTKY95N4MSNG85RPAWJAD0Q5830A3PA88HHRBTFJW83JRRYW34RNQK00KWP13TAW8315ESF46ZH8ZZEGV89R3JHQRYRSKG80V6K3S701KQGSG0C1868C34P1GB0T817GRSY8CRM63J319818QE1JMM0QGAJ0P63J349038M11KAGS180VSN46BJA5TP0100MJGS5DME08000';

function startsWithCsibMagic(bytes: Uint8Array): boolean {
  if (bytes.length < CSIB_MAGIC.length) {
    return false;
  }
  for (let i = 0; i < CSIB_MAGIC.length; i++) {
    if (bytes[i] !== CSIB_MAGIC.charCodeAt(i)) {
      return false;
    }
  }
  return true;
}

export function csibToCrsUrn(csib64: string): string {
  const trimmed = csib64.trim();
  if (trimmed === WGS84_CSIB_BASE64) {
    return `${CSIB_URN_PREFIX}${WGS84_BASE32}`;
  }

  const rawBytes = base64ToBytes(trimmed);
  const compressed = gzip(rawBytes);
  const lengthPrefix = uint32ToLittleEndianBytes(rawBytes.length);
  const payload = new Uint8Array(lengthPrefix.length + compressed.length);
  payload.set(lengthPrefix, 0);
  payload.set(compressed, lengthPrefix.length);

  return `${CSIB_URN_PREFIX}${crockfordBase32Encode(payload)}`;
}

/**
 * Prefixes a profile `csid`. Substitutes the hardcoded WGS84 base32 when the
 * csid decodes to WGS84; any decode failure keeps the original csid.
 */
export function crsUrnFromProfileCsid(csid: string): string {
  try {
    const decoded = crockfordBase32Decode(csid);
    const gzipped = decoded.slice(LENGTH_PREFIX_BYTES);
    const rawBytes = ungzip(gzipped);
    if (!startsWithCsibMagic(rawBytes)) {
      return `${CSIB_URN_PREFIX}${csid}`;
    }
    const csibBase64 = bytesToBase64(rawBytes);
    if (csibBase64 === WGS84_CSIB_BASE64) {
      return `${CSIB_URN_PREFIX}${WGS84_BASE32}`;
    }
    return `${CSIB_URN_PREFIX}${csid}`;
  } catch {
    return `${CSIB_URN_PREFIX}${csid}`;
  }
}
